/* tslint:disable */
import { URI } from '@vscode/base-node/vs/base/common/uri';
import { ResourceMap } from '@vscode/base-node/vs/base/common/map';

import {
    IConfigurationOverrides, IConfigurationData, IConfigurationModel,
} from '../interfaces';
import { ConfigurationModel } from '../models';

export class Configuration {

    private _workspaceConsolidatedConfiguration: ConfigurationModel | null = null;
    private _foldersConsolidatedConfigurations: ResourceMap<ConfigurationModel> = new ResourceMap<ConfigurationModel>();

    constructor(
        private _defaultConfiguration: ConfigurationModel,
        private _userConfiguration: ConfigurationModel,
        private _workspaceConfiguration: ConfigurationModel = new ConfigurationModel(),
        private _folderConfigurations: ResourceMap<ConfigurationModel> = new ResourceMap<ConfigurationModel>(),
        private _memoryConfiguration: ConfigurationModel = new ConfigurationModel(),
        private _memoryConfigurationByResource: ResourceMap<ConfigurationModel> = new ResourceMap<ConfigurationModel>(),
        private _freeze: boolean = true) {
    }

    getValue(section: string, overrides: IConfigurationOverrides, workspace: any /** Workspace */): any {
        const consolidateConfigurationModel = this.getConsolidateConfigurationModel(overrides, workspace);
        return consolidateConfigurationModel.getValue(section);
    }

    updateValue(key: string, value: any, overrides: IConfigurationOverrides = {}): void {
        let memoryConfiguration: ConfigurationModel;
        if (overrides.resource) {
            memoryConfiguration = this._memoryConfigurationByResource.get(overrides.resource);
            if (!memoryConfiguration) {
                memoryConfiguration = new ConfigurationModel();
                this._memoryConfigurationByResource.set(overrides.resource, memoryConfiguration);
            }
        } else {
            memoryConfiguration = this._memoryConfiguration;
        }

        if (value === void 0) {
            memoryConfiguration.removeValue(key);
        } else {
            memoryConfiguration.setValue(key, value);
        }

        if (!overrides.resource) {
            this._workspaceConsolidatedConfiguration = null;
        }
    }

    inspect<C>(key: string, overrides: IConfigurationOverrides, workspace: any /** Workspace */): {
        default: C,
        user: C,
        workspace?: C,
        workspaceFolder?: C
        memory?: C
        value: C,
    } {
        const consolidateConfigurationModel = this.getConsolidateConfigurationModel(overrides, workspace);
        const folderConfigurationModel = this.getFolderConfigurationModelForResource(overrides.resource, workspace);
        const memoryConfigurationModel = overrides.resource ? this._memoryConfigurationByResource.get(overrides.resource) || this._memoryConfiguration : this._memoryConfiguration;
        return {
            default: overrides.overrideIdentifier ? this._defaultConfiguration.freeze().override(overrides.overrideIdentifier).getValue(key) : this._defaultConfiguration.freeze().getValue(key),
            user: overrides.overrideIdentifier ? this._userConfiguration.freeze().override(overrides.overrideIdentifier).getValue(key) : this._userConfiguration.freeze().getValue(key),
            workspace: workspace ? overrides.overrideIdentifier ? this._workspaceConfiguration.freeze().override(overrides.overrideIdentifier).getValue(key) : this._workspaceConfiguration.freeze().getValue(key) : void 0, //Check on workspace exists or not because _workspaceConfiguration is never null
            workspaceFolder: folderConfigurationModel ? overrides.overrideIdentifier ? folderConfigurationModel.freeze().override(overrides.overrideIdentifier).getValue(key) : folderConfigurationModel.freeze().getValue(key) : void 0,
            memory: overrides.overrideIdentifier ? memoryConfigurationModel.freeze().override(overrides.overrideIdentifier).getValue(key) : memoryConfigurationModel.freeze().getValue(key),
            value: consolidateConfigurationModel.getValue(key)
        };
    }

    keys(workspace: any /** Workspace */): {
        default: string[];
        user: string[];
        workspace: string[];
        workspaceFolder: string[];
    } {
        const folderConfigurationModel = this.getFolderConfigurationModelForResource(undefined, workspace);
        return {
            default: this._defaultConfiguration.freeze().keys,
            user: this._userConfiguration.freeze().keys,
            workspace: this._workspaceConfiguration.freeze().keys,
            workspaceFolder: folderConfigurationModel ? folderConfigurationModel.freeze().keys : []
        };
    }

    updateDefaultConfiguration(defaultConfiguration: ConfigurationModel): void {
        this._defaultConfiguration = defaultConfiguration;
        this._workspaceConsolidatedConfiguration = null;
        this._foldersConsolidatedConfigurations.clear();
    }

    updateUserConfiguration(userConfiguration: ConfigurationModel): void {
        this._userConfiguration = userConfiguration;
        this._workspaceConsolidatedConfiguration = null;
        this._foldersConsolidatedConfigurations.clear();
    }

    updateWorkspaceConfiguration(workspaceConfiguration: ConfigurationModel): void {
        this._workspaceConfiguration = workspaceConfiguration;
        this._workspaceConsolidatedConfiguration = null;
        this._foldersConsolidatedConfigurations.clear();
    }

    updateFolderConfiguration(resource: URI, configuration: ConfigurationModel): void {
        this._folderConfigurations.set(resource, configuration);
        this._foldersConsolidatedConfigurations.delete(resource);
    }

    deleteFolderConfiguration(resource: URI): void {
        this.folders.delete(resource);
        this._foldersConsolidatedConfigurations.delete(resource);
    }

    get defaults(): ConfigurationModel {
        return this._defaultConfiguration;
    }

    get user(): ConfigurationModel {
        return this._userConfiguration;
    }

    get workspace(): ConfigurationModel {
        return this._workspaceConfiguration;
    }

    protected get folders(): ResourceMap<ConfigurationModel> {
        return this._folderConfigurations;
    }

    private getConsolidateConfigurationModel(overrides: IConfigurationOverrides, workspace: any /** Workspace */): ConfigurationModel {
        let configurationModel = this.getConsolidatedConfigurationModelForResource(overrides, workspace);
        return overrides.overrideIdentifier ? configurationModel.override(overrides.overrideIdentifier) : configurationModel;
    }

    private getConsolidatedConfigurationModelForResource({ resource }: IConfigurationOverrides, workspace: any /** Workspace */): ConfigurationModel {
        let consolidateConfiguration = this.getWorkspaceConsolidatedConfiguration();

        if (workspace && resource) {
            const root = workspace.getFolder(resource);
            if (root) {
                consolidateConfiguration = this.getFolderConsolidatedConfiguration(root.uri) || consolidateConfiguration;
            }
            const memoryConfigurationForResource = this._memoryConfigurationByResource.get(resource);
            if (memoryConfigurationForResource) {
                consolidateConfiguration = consolidateConfiguration.merge(memoryConfigurationForResource);
            }
        }

        return consolidateConfiguration;
    }

    private getWorkspaceConsolidatedConfiguration(): ConfigurationModel {
        if (!this._workspaceConsolidatedConfiguration) {
            this._workspaceConsolidatedConfiguration = this._defaultConfiguration.merge(this._userConfiguration, this._workspaceConfiguration, this._memoryConfiguration);
            if (this._freeze) {
                this._workspaceConfiguration = this._workspaceConfiguration.freeze();
            }
        }
        return this._workspaceConsolidatedConfiguration;
    }

    private getFolderConsolidatedConfiguration(folder: URI): ConfigurationModel {
        let folderConsolidatedConfiguration = this._foldersConsolidatedConfigurations.get(folder);
        if (!folderConsolidatedConfiguration) {
            const workspaceConsolidateConfiguration = this.getWorkspaceConsolidatedConfiguration();
            const folderConfiguration = this._folderConfigurations.get(folder);
            if (folderConfiguration) {
                folderConsolidatedConfiguration = workspaceConsolidateConfiguration.merge(folderConfiguration);
                if (this._freeze) {
                    folderConsolidatedConfiguration = folderConsolidatedConfiguration.freeze();
                }
                this._foldersConsolidatedConfigurations.set(folder, folderConsolidatedConfiguration);
            } else {
                folderConsolidatedConfiguration = workspaceConsolidateConfiguration;
            }
        }
        return folderConsolidatedConfiguration;
    }

    private getFolderConfigurationModelForResource(resource: URI | undefined, workspace: any /** Workspace */): ConfigurationModel | null {
        if (workspace && resource) {
            const root = workspace.getFolder(resource);
            if (root) {
                return this._folderConfigurations.get(root.uri);
            }
        }
        return null;
    }

    toData(): IConfigurationData {
        return {
            defaults: {
                contents: this._defaultConfiguration.contents,
                overrides: this._defaultConfiguration.overrides,
                keys: this._defaultConfiguration.keys
            },
            user: {
                contents: this._userConfiguration.contents,
                overrides: this._userConfiguration.overrides,
                keys: this._userConfiguration.keys
            },
            workspace: {
                contents: this._workspaceConfiguration.contents,
                overrides: this._workspaceConfiguration.overrides,
                keys: this._workspaceConfiguration.keys
            },
            folders: this._folderConfigurations.keys().reduce((result, folder) => {
                const { contents, overrides, keys } = this._folderConfigurations.get(folder);
                result[folder.toString()] = { contents, overrides, keys };
                return result;
            }, Object.create({})),
            isComplete: true
        };
    }

    allKeys(workspace: any /** Workspace */): string[] {
        let keys = this.keys(workspace);
        let all = [...keys.default];
        const addKeys = (keys) => {
            for (const key of keys) {
                if (all.indexOf(key) === -1) {
                    all.push(key);
                }
            }
        };
        addKeys(keys.user);
        addKeys(keys.workspace);
        for (const resource of this.folders.keys()) {
            addKeys(this.folders.get(resource).keys);
        }
        return all;
    }
}